import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSelection } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';

import { ProductPicker, type StoreProductRow } from '@/features/resource-pickers';

export interface AddCollectionProductsDialogProps {
  storeId: string;
  collectionId: string;
  /** Master product ids already in the list; shown ticked and locked. */
  excludeIds: ReadonlySet<string>;
  /** Products the collection can still take (its cap less its members): "Select all" stops there. */
  remaining: number;
  /** The picked products: ticked ids, or every matching store product as a query selection. */
  onAdd: (selection: ProductSelection, rows: StoreProductRow[]) => void;
  disabled?: boolean;
}

/**
 * "Add products" on a manual collection: the store catalog in the product picker. The list flags
 * the collection's members (`memberOf`), and "Select all N matching" hands the filter to the
 * append endpoint instead of loading every page.
 */
export function AddCollectionProductsDialog({
  storeId,
  collectionId,
  excludeIds,
  remaining,
  onAdd,
  disabled,
}: AddCollectionProductsDialogProps) {
  const { t } = useTranslation('collections');
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Plus aria-hidden />
        {t('products.add')}
      </Button>
      <ProductPicker
        open={open}
        onOpenChange={setOpen}
        scope={{ kind: 'store', storeId }}
        title={t('products.dialog.title')}
        memberOf={{ kind: 'collection', id: collectionId }}
        lockedIds={excludeIds}
        lockedLabel={t('products.dialog.alreadyAdded')}
        // The total counts the members too, so "Select all" may stop short of what would fit.
        selectAllMatching={{ max: Math.max(remaining, 0) }}
        onConfirm={(selection, productSelection) => onAdd(productSelection, selection.items)}
      />
    </>
  );
}
